import type { MapEntityPath, MapPathStyle, MapPathType, MapRoadQuality } from '@common/schemas/entities/location.schema';
import { MAP_PATH_TYPES, MAP_ROAD_QUALITIES } from '@common/schemas/entities/location.schema';
import { useCallback, useEffect, useRef, useState } from 'react';

export { MAP_PATH_TYPES, MAP_ROAD_QUALITIES };
export type { MapPathType, MapRoadQuality };

const generateUid = () => Math.random().toString(36).substring(2, 9);

type Vertex = { x: number; y: number };

/** Default style presets keyed by path type. */
export const PATH_TYPE_DEFAULTS: Record<MapPathType, MapPathStyle> = {
    road: { strokeColor: '#8B7355', strokeWidth: 3, opacity: 1, dashed: false, lineCap: 'round', lineJoin: 'round' },
    river: { strokeColor: '#2b4e5d', strokeWidth: 2.5, opacity: 1, dashed: false, lineCap: 'round', lineJoin: 'round' },
    route: { strokeColor: '#e67e22', strokeWidth: 2, opacity: 1, dashed: true, lineCap: 'round', lineJoin: 'round' },
    custom: { strokeColor: '#9b59b6', strokeWidth: 2, opacity: 1, dashed: false, lineCap: 'round', lineJoin: 'round' },
};

/** Road quality adjustments applied on top of the road preset. */
const ROAD_QUALITY_OVERRIDES: Record<MapRoadQuality, Partial<MapPathStyle>> = {
    trail: { strokeWidth: 1.5, dashed: true, strokeColor: '#a08060' },
    dirt: { strokeWidth: 2, dashed: false, strokeColor: '#9B7B5B' },
    cobbled: { strokeWidth: 2.5, dashed: false, strokeColor: '#7a6a5a' },
    paved: { strokeWidth: 3, dashed: false, strokeColor: '#6B5B4B' },
};

function buildDefaultStyle(pathType: MapPathType, roadQuality?: MapRoadQuality): MapPathStyle {
    const base = { ...PATH_TYPE_DEFAULTS[pathType] };
    if (pathType === 'road' && roadQuality) {
        return { ...base, ...ROAD_QUALITY_OVERRIDES[roadQuality] };
    }
    return base;
}

export function useMapPaths(initialPaths?: MapEntityPath[] | null) {
    const [mapEntityPaths, setMapEntityPaths] = useState<MapEntityPath[]>([]);
    const [isDrawingPath, setIsDrawingPath] = useState(false);
    const [selectedPathUid, setSelectedPathUid] = useState<string | null>(null);

    // In-progress drawing state
    const [inProgressPathVertices, setInProgressPathVertices] = useState<Vertex[]>([]);
    const [pathCursorVertex, setPathCursorVertex] = useState<Vertex | null>(null);

    // Sync from DB
    useEffect(() => {
        setMapEntityPaths(initialPaths ?? []);
    }, [initialPaths]);

    // --- Drawing ---
    const addPathVertex = useCallback((x: number, y: number) => {
        setInProgressPathVertices(prev => [...prev, { x, y }]);
    }, []);

    const finishPathDrawing = useCallback((pathType: MapPathType = 'road', roadQuality?: MapRoadQuality, finalSnapPoint?: Vertex) => {
        setInProgressPathVertices(prev => {
            if (prev.length >= 2) {
                const uid = generateUid();
                const style = buildDefaultStyle(pathType, roadQuality);
                
                let finalVertices = [...prev];
                if (pathType === 'river') {
                    const lastIdx = finalVertices.length - 1;
                    const lastV = finalVertices[lastIdx];
                    let closestX = lastV.x;
                    let closestY = lastV.y;
                    let minDist = Infinity;
                    const snapThreshold = 18;
                    for (const p of mapEntityPaths) {
                        if (p.pathType !== 'river') continue;
                        for (const v of p.vertices) {
                            const d = Math.hypot(lastV.x - v.x, lastV.y - v.y);
                            if (d < minDist && d < snapThreshold) {
                                minDist = d; closestX = v.x; closestY = v.y;
                            }
                        }
                        for (let i = 0; i < p.vertices.length - 1; i++) {
                            const a = p.vertices[i];
                            const b = p.vertices[i + 1];
                            const dx = b.x - a.x; const dy = b.y - a.y;
                            const lenSq = dx * dx + dy * dy;
                            if (lenSq === 0) continue;
                            const t = Math.max(0, Math.min(1, ((lastV.x - a.x) * dx + (lastV.y - a.y) * dy) / lenSq));
                            const projX = a.x + t * dx;
                            const projY = a.y + t * dy;
                            const d = Math.hypot(lastV.x - projX, lastV.y - projY);
                            if (d < minDist && d < snapThreshold) {
                                minDist = d; closestX = projX; closestY = projY;
                            }
                        }
                    }
                    finalVertices[lastIdx] = { x: closestX, y: closestY };
                }

                const newPath: MapEntityPath = { uid, pathType, roadQuality, vertices: finalVertices, style };
                setMapEntityPaths(paths => [...paths, newPath]);
                setSelectedPathUid(uid);
            }
            return [];
        });
        setPathCursorVertex(null);
        setIsDrawingPath(false);
    }, [mapEntityPaths]);

    const cancelPathDrawing = useCallback(() => {
        setInProgressPathVertices([]);
        setPathCursorVertex(null);
        setIsDrawingPath(false);
    }, []);

    // --- Keyboard ---
    useEffect(() => {
        if (!isDrawingPath) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.preventDefault(); cancelPathDrawing(); }
            if (e.key === 'Enter') { e.preventDefault(); finishPathDrawing(); }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isDrawingPath, cancelPathDrawing, finishPathDrawing]);



    // --- CRUD ---
    const deletePath = useCallback((uid: string) => {
        setMapEntityPaths(prev => prev.filter(p => p.uid !== uid));
        setSelectedPathUid(prev => (prev === uid ? null : prev));
    }, []);

    const updatePathStyle = useCallback((uid: string, style: Partial<MapPathStyle>) => {
        setMapEntityPaths(prev =>
            prev.map(p => p.uid === uid ? { ...p, style: { ...p.style, ...style } } : p)
        );
    }, []);

    const updatePathMeta = useCallback((uid: string, meta: { name?: string; description?: string }) => {
        setMapEntityPaths(prev =>
            prev.map(p => p.uid === uid ? { ...p, ...meta } : p)
        );
    }, []);

    const updatePathType = useCallback((uid: string, pathType: MapPathType, roadQuality?: MapRoadQuality) => {
        const style = buildDefaultStyle(pathType, roadQuality);
        setMapEntityPaths(prev =>
            prev.map(p => p.uid === uid ? { ...p, pathType, roadQuality, style } : p)
        );
    }, []);

    // Expose so the active drawing state can be captured in finishPathDrawing
    const activePathTypeRef = useRef<{ pathType: MapPathType; roadQuality?: MapRoadQuality }>({
        pathType: 'road',
    });

    const startDrawingPath = useCallback((pathType: MapPathType, roadQuality?: MapRoadQuality) => {
        activePathTypeRef.current = { pathType, roadQuality };
        setIsDrawingPath(true);
        setSelectedPathUid(null);
    }, []);

    const finishPathDrawingWithActiveType = useCallback((finalSnapPoint?: Vertex) => {
        const { pathType, roadQuality } = activePathTypeRef.current;
        finishPathDrawing(pathType, roadQuality, finalSnapPoint);
    }, [finishPathDrawing]);

    const moveVertex = useCallback((uid: string, index: number, x: number, y: number) => {
        setMapEntityPaths(prev =>
            prev.map(p => {
                if (p.uid !== uid) return p;
                const newVertices = [...p.vertices];
                newVertices[index] = { x, y };
                return { ...p, vertices: newVertices };
            })
        );
    }, []);

    const insertVertex = useCallback((uid: string, index: number, x: number, y: number) => {
        setMapEntityPaths(prev =>
            prev.map(p => {
                if (p.uid !== uid) return p;
                const newVertices = [...p.vertices];
                newVertices.splice(index, 0, { x, y });
                return { ...p, vertices: newVertices };
            })
        );
    }, []);

    const deleteVertex = useCallback((uid: string, index: number) => {
        setMapEntityPaths(prev =>
            prev.map(p => {
                if (p.uid !== uid) return p;
                if (p.vertices.length <= 2) return p; // Don't allow paths with less than 2 vertices
                const newVertices = p.vertices.filter((_, idx) => idx !== index);
                return { ...p, vertices: newVertices };
            })
        );
    }, []);

    return {
        mapEntityPaths,
        setMapEntityPaths,
        isDrawingPath,
        setIsDrawingPath,
        selectedPathUid,
        setSelectedPathUid,
        inProgressPathVertices,
        pathCursorVertex,
        setPathCursorVertex,
        addPathVertex,
        finishPathDrawing: finishPathDrawingWithActiveType,
        cancelPathDrawing,
        startDrawingPath,
        deletePath,
        updatePathStyle,
        updatePathMeta,
        updatePathType,
        moveVertex,
        insertVertex,
        deleteVertex,
        activePathTypeRef,
    };
}
